import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi } from "@/api/admin.ts";

const PAGE_SIZE = 50;

const ACTION_COLORS: Record<string, "info" | "warning" | "danger" | "success" | "muted"> = {
  "user.update": "info",
  "user.credit_adjust": "warning",
  "gateway.create": "success",
  "gateway.update": "info",
  "gateway.delete": "danger",
  "settings.update": "warning",
};

export function AdminAuditPage() {
  const [offset, setOffset] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit", offset],
    queryFn: () => adminApi.auditLog({ limit: PAGE_SIZE, offset }),
  });

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6 flex items-center gap-3">
        <ScrollText className="h-6 w-6 text-fgMuted" />
        <div>
          <h1 className="text-3xl font-bold text-fg">Audit Log</h1>
          <p className="text-fgMuted">All administrative actions are recorded here</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !data || data.entries.length === 0 ? (
            <p className="py-12 text-center text-fgMuted">No audit entries yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-fgMuted">
                    <th className="px-6 py-3">Timestamp</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Target</th>
                    <th className="px-4 py-3">Details</th>
                    <th className="px-4 py-3">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-surface2/20">
                      <td className="whitespace-nowrap px-6 py-3 text-fgMuted">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={ACTION_COLORS[entry.action] ?? "muted"}>
                          {entry.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-fg">
                        {entry.targetType && (
                          <span className="text-fgMuted">
                            {entry.targetType}
                            {entry.targetId && <span className="ml-1 font-mono text-xs">{entry.targetId.slice(0, 8)}…</span>}
                          </span>
                        )}
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 text-xs text-fgMuted font-mono">
                        {entry.details ? JSON.stringify(entry.details) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-fgMuted font-mono">
                        {entry.ipAddress ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.pagination.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-fgMuted">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.pagination.total)} of {data.pagination.total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= data.pagination.total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
